import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import { fetchSiteSettings } from '../services/adminApi';
import { SiteSettingsForm } from '../components/admin/SiteSettingsForm';
import { SiteSettingsAuditLog } from '../components/admin/SiteSettingsAuditLog';
import { DeviceRatingsTable } from '../components/admin/DeviceRatingsTable';
import { DeviceRatingsAuditLog } from '../components/admin/DeviceRatingsAuditLog';

type SettingsTab = 'metadata' | 'devices';

export function AdminSiteSettings() {
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab: SettingsTab = searchParams.get('tab') === 'devices' ? 'devices' : 'metadata';
  const [activeTab, setActiveTab] = useState<SettingsTab>(initialTab);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(null);

  // Keep URL in sync so Devices tab is deep-linkable (?tab=devices).
  useEffect(() => {
    const current = searchParams.get('tab');
    if (activeTab === 'devices' && current !== 'devices') {
      setSearchParams({ tab: 'devices' }, { replace: true });
    } else if (activeTab === 'metadata' && current === 'devices') {
      setSearchParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const { data: site, isLoading, isError } = useQuery({
    queryKey: ['site-settings', id],
    queryFn: () => fetchSiteSettings(id),
    enabled: !!id,
  });

  if (isLoading) {
    return <div className="p-6 text-sm text-ems-text-muted">Loading site...</div>;
  }
  if (isError || !site) {
    return <div className="p-6 text-sm text-ems-red">Failed to load site.</div>;
  }

  return (
    <div className="space-y-4 p-6">
      <Link
        to={`/project/${id}`}
        className="inline-flex items-center gap-1 text-sm text-ems-text-muted hover:text-ems-green"
      >
        <ChevronLeft className="w-4 h-4" /> Back to Site Overview
      </Link>
      <div>
        <h1 className="text-lg font-bold text-ems-text-primary">
          Site Settings — {site.name}
        </h1>
        <p className="text-sm text-ems-text-muted">
          Edit site metadata and per-device ratings. All changes are recorded in the audit log.
        </p>
      </div>

      <div className="flex items-center gap-1 border-b border-ems-border">
        <TabButton
          isActive={activeTab === 'metadata'}
          onClick={() => setActiveTab('metadata')}
        >
          Site Metadata
        </TabButton>
        <TabButton
          isActive={activeTab === 'devices'}
          onClick={() => setActiveTab('devices')}
        >
          Devices
        </TabButton>
      </div>

      {activeTab === 'metadata' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <SiteSettingsForm site={site} />
          </div>
          <div className="lg:col-span-1">
            <SiteSettingsAuditLog siteId={id} />
          </div>
        </div>
      )}

      {activeTab === 'devices' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <DeviceRatingsTable
              siteId={id}
              selectedDeviceId={selectedDeviceId}
              onSelectDevice={setSelectedDeviceId}
            />
          </div>
          <div className="lg:col-span-1">
            <DeviceRatingsAuditLog siteId={id} deviceId={selectedDeviceId} />
          </div>
        </div>
      )}
    </div>
  );
}

function TabButton({
  isActive,
  onClick,
  children,
}: {
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
        isActive
          ? 'text-ems-green border-ems-green'
          : 'text-ems-text-muted border-transparent hover:text-ems-text-secondary'
      }`}
    >
      {children}
    </button>
  );
}
